<script setup lang="ts">
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiStepper from '@/components/pos-v2/ui/PosUiStepper.vue';
  import type { IReservationDurationRule } from '@/interfaces/IReservationSettings';
  const props = defineProps<{ value: IReservationDurationRule[] }>();
  const emit = defineEmits<{
    (event: 'input', value: IReservationDurationRule[]): void;
  }>();
  function update(
    index: number,
    key: keyof IReservationDurationRule,
    value: number
  ) {
    emit(
      'input',
      props.value.map((rule, i) =>
        i === index ? { ...rule, [key]: value } : rule
      )
    );
  }
  function add() {
    const last = props.value[props.value.length - 1]?.maxParty ?? 0;
    if (last >= 200) return;
    emit('input', [
      ...props.value,
      { maxParty: Math.min(200, last + 2), minutes: 90 },
    ]);
  }
  function remove(index: number) {
    emit(
      'input',
      props.value.filter((_, i) => i !== index)
    );
  }
</script>

<template>
  <div class="duration-rules">
    <div v-for="(rule, index) in value" :key="index" class="rule">
      <div class="rule-people">
        <span>{{
          $t('reservations.settings.durationRules.upTo', {
            count: rule.maxParty,
          })
        }}</span>
        <PosUiStepper
          :value="rule.maxParty"
          :min="index ? value[index - 1].maxParty + 1 : 1"
          :max="value[index + 1] ? value[index + 1].maxParty - 1 : 200"
          @input="update(index, 'maxParty', $event)"
        />
      </div>
      <div class="rule-duration">
        <span>{{
          $t('reservations.common.minutes', { minutes: rule.minutes })
        }}</span>
        <PosUiStepper
          :value="rule.minutes"
          :min="30"
          :max="360"
          :step="15"
          @input="update(index, 'minutes', $event)"
        />
      </div>
      <PosUiButton
        color="neutral"
        variant="ghost"
        icon="close"
        :aria-label="$t('delete')"
        @click="remove(index)"
      />
    </div>
    <PosUiButton
      variant="outline"
      color="neutral"
      icon="plus"
      :disabled="value.length > 0 && value[value.length - 1].maxParty >= 200"
      @click="add"
    >
      {{ $t('reservations.settings.durationRules.add') }}
    </PosUiButton>
    <p>{{ $t('reservations.settings.durationRules.hint') }}</p>
  </div>
</template>

<style scoped lang="scss">
  .duration-rules {
    .rule {
      @apply tw-mb-3 tw-flex tw-flex-wrap tw-items-end tw-rounded-lg tw-bg-canvas tw-p-3;
    }
    .rule-people,
    .rule-duration {
      @apply tw-mr-4 tw-flex tw-flex-col tw-pb-1;
      > span {
        @apply tw-mb-1 tw-text-xs tw-text-neutral-600;
      }
    }
    p {
      @apply tw-mb-0 tw-mt-2 tw-text-xs tw-text-neutral-500;
    }
  }
</style>
